// Deterministic, hand-drawn-style geometry diagrams for maths questions.
// It reads the numbers straight from the question text and draws a clean,
// labelled figure so a child can VISUALISE the problem — it never shows or
// hints at the computed answer (only the values given in the question).
// parseGeometry(text) returns a shape descriptor or null (null -> caller can
// fall back to the AI illustration).

const N = "(\\d+(?:\\.\\d+)?)";
const U = "(mm|cm|km|m|metres?|meters?|centimet(?:re|er)s?)";

const shortUnit = (u) => {
  if (!u) return "";
  const s = u.toLowerCase();
  if (s.startsWith("mm")) return "mm";
  if (s.startsWith("cm") || s.startsWith("centim")) return "cm";
  if (s.startsWith("km")) return "km";
  return "m";
};

export function parseGeometry(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();

  // 1) Rectangle / square with a PATH or BORDER around it
  if (/(path|border|frame|margin|walkway|footpath)/.test(t)) {
    const dim = t.match(new RegExp(`${N}\\s*${U}?\\s*(?:by|×|x)\\s*${N}\\s*${U}?`));
    const sq = t.match(new RegExp(`square[^.]*?side[^0-9]*?${N}\\s*${U}?`)) ||
               t.match(new RegExp(`${N}\\s*${U}?\\s*(?:m|cm|mm|km)?\\s*square`));
    let W, L, unit;
    if (dim) { W = +dim[1]; L = +dim[3]; unit = shortUnit(dim[2] || dim[4]); }
    else if (sq) { W = L = +sq[1]; unit = shortUnit(sq[2]); }
    const pw = t.match(new RegExp(`${N}\\s*${U}?[\\s-]*(?:wide\\s+)?(?:path|border|frame|margin|walkway|footpath)`)) ||
               t.match(new RegExp(`(?:path|border|frame|margin|walkway|footpath)[^0-9]{0,18}?(?:width\\s*(?:of\\s*)?)?${N}\\s*${U}?`));
    if (W && L && pw) {
      const outside = /outside|outer|around the outside|external/.test(t);
      return { type: "rectpath", W, L, path: +pw[1], unit: unit || shortUnit(pw[2]), inside: !outside };
    }
  }

  // 1b) Composite: L-shape (two rectangles) or a shape with a corner cut out
  {
    if (/l-?shap/.test(t)) {
      const pairs = [...t.matchAll(new RegExp(`${N}\\s*${U}?\\s*(?:x|×|by)\\s*${N}\\s*${U}?`, "g"))];
      if (pairs.length >= 2) {
        const u = shortUnit(pairs[0][2] || pairs[0][4] || pairs[1][2] || pairs[1][4]);
        return { type: "lshape", r1: [+pairs[0][1], +pairs[0][3]], r2: [+pairs[1][1], +pairs[1][3]], unit: u };
      }
    }
    if (/cut (?:out )?from|cut (?:from|out)|removed from|cut away/.test(t)) {
      const sq = t.match(new RegExp(`square of side\\s*${N}\\s*${U}?[\\s\\S]*?square of side\\s*${N}\\s*${U}?`));
      if (sq) return { type: "cutout", W: +sq[1], L: +sq[1], cw: +sq[3], ch: +sq[3], unit: shortUnit(sq[2] || sq[4]) };
      const rc = t.match(new RegExp(`${N}\\s*${U}?\\s*(?:by|×|x)\\s*${N}\\s*${U}?[\\s\\S]{0,40}?${N}\\s*${U}?\\s*(?:by|×|x)\\s*${N}\\s*${U}?`));
      if (rc && /(rectang|square|corner)/.test(t)) return { type: "cutout", W: +rc[1], L: +rc[3], cw: +rc[5], ch: +rc[7], unit: shortUnit(rc[2] || rc[4]) };
    }
  }

  // 2) Circle
  if (/circ/.test(t)) {
    const r = t.match(new RegExp(`radius\\s*(?:of\\s*)?${N}\\s*${U}?`));
    const d = t.match(new RegExp(`diameter\\s*(?:of\\s*)?${N}\\s*${U}?`));
    if (r || d) return { type: "circle", r: r ? +r[1] : null, d: d ? +d[1] : null, unit: shortUnit((r && r[2]) || (d && d[2])) };
  }

  // 3) Triangle
  if (/triangle|triangular/.test(t)) {
    const b = t.match(new RegExp(`base\\s*(?:of\\s*)?${N}\\s*${U}?`));
    const h = t.match(new RegExp(`(?:height|high|perpendicular height)\\s*(?:of\\s*)?${N}\\s*${U}?`));
    const sides = [...t.matchAll(new RegExp(`${N}\\s*${U}`, "g"))].map((m) => ({ v: +m[1], u: shortUnit(m[2]) }));
    if (b || h) return { type: "triangle", base: b ? +b[1] : null, height: h ? +h[1] : null, unit: shortUnit((b && b[2]) || (h && h[2])) };
    if (sides.length >= 3) return { type: "triangle", sides: sides.slice(0, 3).map((s) => s.v), unit: sides[0].u };
    return { type: "triangle", unit: sides[0] ? sides[0].u : "" };
  }

  // 4) Square (a square SHAPE — not "square metres" / "square numbers")
  if (/\bsquare\b/.test(t) && !/square\s*(met|cent|milli|kilo|inch|f(?:ee|oo)t|unit|mile|yard)/.test(t) && !/square numbers?/.test(t)) {
    const m = t.match(new RegExp(`side\\s*(?:of\\s*)?(?:length\\s*)?${N}\\s*${U}?`)) ||
              t.match(new RegExp(`${N}\\s*${U}?[\\s-]*(?:m|cm|mm|km)?\\s*square`));
    if (m) return { type: "square", s: +m[1], unit: shortUnit(m[2]) };
    return { type: "square", s: null, unit: "" };
  }

  // 5) Rectangle — explicit, or any "N unit by N unit" measurement problem
  {
    const by = t.match(new RegExp(`${N}\\s*${U}\\s*(?:by|×|x)\\s*${N}\\s*${U}?`)) ||
               t.match(new RegExp(`${N}\\s*${U}?\\s*(?:by|×|x)\\s*${N}\\s*${U}`));
    const lw = t.match(new RegExp(`${N}\\s*${U}?\\s*(?:long|length)[\\s\\S]{0,24}?${N}\\s*${U}?\\s*(?:wide|width|broad)`));
    const ctx = /(area|perimeter|rectang|floor|lawn|garden|field|room|carpet|\btile|paving|paved|fenc|\bwall\b|plot|sheet|\bcover|surface|paint|\bmat\b|poster|screen|rug)/.test(t);
    if ((by || lw) && ctx) {
      const m = by || lw;
      const unit = shortUnit(by ? (by[2] || by[4]) : (lw[2] || lw[4]));
      return { type: "rect", W: +m[1], L: +m[3], unit, ask: /perimeter/.test(t) ? "perimeter" : "area" };
    }
  }

  return null;
}

const lbl = (v, u) => `${v}${u ? " " + u : ""}`;

// A short double-headed dimension arrow with a label.
const DimH = ({ x1, x2, y, text }) => (
  <g stroke="#0F172A" strokeWidth="1.5">
    <line x1={x1} y1={y} x2={x2} y2={y} markerStart="url(#arr)" markerEnd="url(#arr)" />
    <text x={(x1 + x2) / 2} y={y - 5} textAnchor="middle" fontSize="12" fontWeight="700" fill="#0F172A" stroke="none">{text}</text>
  </g>
);
const DimV = ({ x, y1, y2, text }) => (
  <g stroke="#0F172A" strokeWidth="1.5">
    <line x1={x} y1={y1} x2={x} y2={y2} markerStart="url(#arr)" markerEnd="url(#arr)" />
    <text x={x - 6} y={(y1 + y2) / 2} textAnchor="middle" fontSize="12" fontWeight="700" fill="#0F172A" stroke="none" transform={`rotate(-90 ${x - 6} ${(y1 + y2) / 2})`}>{text}</text>
  </g>
);

function Rect({ spec }) {
  const { W, L, unit, ask } = spec;
  const maxW = 210, maxH = 120;
  const sc = Math.min(maxW / W, maxH / L);
  const w = W * sc, h = L * sc;
  const x = (300 - w) / 2, y = (176 - h) / 2 + 6;
  return (
    <>
      <rect x={x} y={y} width={w} height={h} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" rx="4" />
      <DimH x1={x} x2={x + w} y={y - 12} text={lbl(W, unit)} />
      <DimV x={x - 14} y1={y} y2={y + h} text={lbl(L, unit)} />
      <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill="#1D4ED8">{ask === "perimeter" ? "perimeter = ?" : "area = ?"}</text>
    </>
  );
}

function RectPath({ spec }) {
  const { W, L, path, unit, inside } = spec;
  const outW = inside ? W : W + 2 * path;
  const outL = inside ? L : L + 2 * path;
  const maxW = 200, maxH = 116;
  const sc = Math.min(maxW / outW, maxH / outL);
  const OW = outW * sc, OL = outL * sc, P = Math.max(6, path * sc);
  const x = (300 - OW) / 2, y = (176 - OL) / 2 + 6;
  // garden rectangle we label with the given W×L
  const gx = inside ? x : x + P, gy = inside ? y : y + P, gw = W * sc, gh = L * sc;
  return (
    <>
      {/* shaded ring = the path */}
      <rect x={x} y={y} width={OW} height={OL} fill="#FDE68A" stroke="#0F172A" strokeWidth="3" rx="3" />
      <rect x={x + P} y={y + P} width={OW - 2 * P} height={OL - 2 * P} fill="#DCFCE7" stroke="#0F172A" strokeWidth="2" />
      {/* given garden dimensions */}
      <DimH x1={gx} x2={gx + gw} y={y - 12} text={lbl(W, unit)} />
      <DimV x={x - 14} y1={gy} y2={gy + gh} text={lbl(L, unit)} />
      {/* path width arrow on the top ring */}
      <g stroke="#B45309" strokeWidth="1.5">
        <line x1={x + OW - P} y1={y} x2={x + OW - P} y2={y + P} markerStart="url(#arrO)" markerEnd="url(#arrO)" />
      </g>
      <text x={x + OW + 4} y={y + P + 4} fontSize="11" fontWeight="700" fill="#B45309">{lbl(path, unit)} path</text>
    </>
  );
}

function Square({ spec }) {
  const { s, unit } = spec;
  const side = 108;
  const x = (300 - side) / 2, y = 30;
  return (
    <>
      <rect x={x} y={y} width={side} height={side} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" rx="4" />
      {s != null && <DimH x1={x} x2={x + side} y={y - 12} text={lbl(s, unit)} />}
      {s != null && <DimV x={x - 14} y1={y} y2={y + side} text={lbl(s, unit)} />}
    </>
  );
}

function Triangle({ spec }) {
  const { base, height, sides, unit } = spec;
  const bw = 190, hh = 118;
  const x = (300 - bw) / 2, yB = 30 + hh, apexX = x + bw * 0.42;
  return (
    <>
      <polygon points={`${x},${yB} ${x + bw},${yB} ${apexX},30`} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" strokeLinejoin="round" />
      {/* height (dashed perpendicular) */}
      {height != null && (
        <>
          <line x1={apexX} y1="30" x2={apexX} y2={yB} stroke="#B45309" strokeWidth="1.5" strokeDasharray="4 3" />
          <rect x={apexX} y={yB - 9} width="9" height="9" fill="none" stroke="#B45309" strokeWidth="1" />
          <text x={apexX + 6} y={(30 + yB) / 2} fontSize="12" fontWeight="700" fill="#B45309">{lbl(height, unit)}</text>
        </>
      )}
      {base != null && <DimH x1={x} x2={x + bw} y={yB + 16} text={lbl(base, unit)} />}
      {sides && (
        <>
          <text x={(x + apexX) / 2 - 14} y={(yB + 30) / 2} fontSize="11" fontWeight="700" fill="#0F172A">{lbl(sides[0], unit)}</text>
          <text x={(x + bw + apexX) / 2 + 4} y={(yB + 30) / 2} fontSize="11" fontWeight="700" fill="#0F172A">{lbl(sides[1], unit)}</text>
          <DimH x1={x} x2={x + bw} y={yB + 16} text={lbl(sides[2], unit)} />
        </>
      )}
    </>
  );
}

function Circle({ spec }) {
  const { r, d, unit } = spec;
  const R = 62, cx = 150, cy = 96;
  return (
    <>
      <circle cx={cx} cy={cy} r={R} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" />
      <circle cx={cx} cy={cy} r="3" fill="#0F172A" />
      {r != null && !d && (
        <>
          <line x1={cx} y1={cy} x2={cx + R} y2={cy} stroke="#B45309" strokeWidth="1.5" />
          <text x={cx + R / 2} y={cy - 6} textAnchor="middle" fontSize="12" fontWeight="700" fill="#B45309">r = {lbl(r, unit)}</text>
        </>
      )}
      {d != null && (
        <>
          <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke="#B45309" strokeWidth="1.5" markerStart="url(#arrO)" markerEnd="url(#arrO)" />
          <text x={cx} y={cy - 6} textAnchor="middle" fontSize="12" fontWeight="700" fill="#B45309">d = {lbl(d, unit)}</text>
        </>
      )}
    </>
  );
}

function LShape({ spec }) {
  const [W1, H1] = spec.r1, [W2, H2] = spec.r2, unit = spec.unit;
  const totW = Math.max(W1, W2), totH = H1 + H2;
  const sc = Math.min(190 / totW, 108 / totH);
  const wB = W1 * sc, hB = H1 * sc, wT = W2 * sc, hT = H2 * sc;
  const x = (300 - Math.max(wB, wT)) / 2 + 6, y = 28;
  const yMid = y + hT, yBot = y + hT + hB;
  const pts = [[x, y], [x + wT, y], [x + wT, yMid], [x + wB, yMid], [x + wB, yBot], [x, yBot]];
  return (
    <>
      <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" strokeLinejoin="round" />
      <line x1={x} y1={yMid} x2={x + Math.min(wB, wT)} y2={yMid} stroke="#64748B" strokeWidth="1.5" strokeDasharray="4 3" />
      <DimH x1={x} x2={x + wT} y={y - 12} text={lbl(W2, unit)} />
      <DimV x={x - 14} y1={y} y2={yMid} text={lbl(H2, unit)} />
      <DimV x={x - 14} y1={yMid} y2={yBot} text={lbl(H1, unit)} />
      <DimH x1={x} x2={x + wB} y={yBot + 16} text={lbl(W1, unit)} />
    </>
  );
}

function Cutout({ spec }) {
  const { W, L, cw, ch, unit } = spec;
  const sc = Math.min(190 / W, 108 / L);
  const ow = W * sc, oh = L * sc;
  const nw = Math.min(cw * sc, ow * 0.92), nh = Math.min(ch * sc, oh * 0.92);
  const x = (300 - ow) / 2, y = 30;
  const pts = [[x, y], [x + ow - nw, y], [x + ow - nw, y + nh], [x + ow, y + nh], [x + ow, y + oh], [x, y + oh]];
  return (
    <>
      {/* removed corner shown as a dashed ghost square */}
      <rect x={x + ow - nw} y={y} width={nw} height={nh} fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1.2" strokeDasharray="4 3" />
      <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="#DBEAFE" stroke="#0F172A" strokeWidth="3" strokeLinejoin="round" />
      <DimH x1={x} x2={x + ow} y={y + oh + 16} text={lbl(W, unit)} />
      <DimV x={x - 14} y1={y} y2={y + oh} text={lbl(L, unit)} />
      <text x={x + ow - nw / 2} y={y - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#B45309">{lbl(cw, unit)}</text>
      <text x={x + ow + 5} y={y + nh / 2 + 4} fontSize="11" fontWeight="700" fill="#B45309">{lbl(ch, unit)}</text>
    </>
  );
}

export default function GeometryDiagram({ spec, compact }) {
  if (!spec) return null;
  let Shape = null;
  if (spec.type === "rectpath") Shape = RectPath;
  else if (spec.type === "rect") Shape = Rect;
  else if (spec.type === "square") Shape = Square;
  else if (spec.type === "triangle") Shape = Triangle;
  else if (spec.type === "circle") Shape = Circle;
  else if (spec.type === "lshape") Shape = LShape;
  else if (spec.type === "cutout") Shape = Cutout;
  if (!Shape) return null;
  return (
    <div className="w-full mx-auto mb-3 rounded-2xl border-4 border-slate-900 bg-white overflow-hidden"
      style={{ boxShadow: "4px 4px 0 0 #0F172A", maxHeight: compact ? "24vh" : "30vh" }}
      data-testid="quiz-diagram">
      <svg viewBox="0 0 300 190" className="w-full h-auto block" style={{ maxHeight: compact ? "22vh" : "28vh" }} preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arr" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <path d="M1,1 L5,4 L1,7" fill="none" stroke="#0F172A" strokeWidth="1.3" />
          </marker>
          <marker id="arrO" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <path d="M1,1 L5,4 L1,7" fill="none" stroke="#B45309" strokeWidth="1.3" />
          </marker>
        </defs>
        <Shape spec={spec} />
      </svg>
    </div>
  );
}
